import React, { useEffect, useMemo, useState } from "react";
import { InputNumberFormat, format, unformat } from "@react-input/number-format";
import PaymentStepper from "../../../../component/pay/PaymentStepper";
import OnboardingLayout from "../../../../component/auth/OnboardingLayout";
import { ArrowRight2, Timer1 } from "iconsax-reactjs";
import { useDispatch, useSelector } from "react-redux";
import CurrencyToggle from "../../../../component/currencyToggle";
import SelectNetworkModal from "../../../../component/wallet/SelectNetworkModal";
import { useNavigate } from "react-router-dom";
import {
    resetCryptoTransfer,
    setCryptoAmount,
    setCryptoNetwork,
    setCryptoToken,
} from "../../../../redux/slices/cryptoTransferSlice";
import useAccountBalance from "../../../../hooks/useAccountBalance";
import { fetchCoinNetworks, fetchTransferFees } from "../../../../api/fetchers";
import { resetCryptoFiatTransfer } from "../../../../redux/slices/cryptoFiatTransferSlice";
import { resetTransfer } from "../../../../redux/slices/tradevuTransferSlice";
import { resetUsdTransfer } from "../../../../redux/slices/usdTransferSlice";
import { resetBankTransfer } from "../../../../redux/slices/bankTransferSlice";
import InfoTooltip from "../../../../component/InfoTooltip";

const CryptoWalletTransfer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { balance, loading } = useAccountBalance();

    const [amount, setAmount] = useState("");
    const [network, setNetwork] = useState(null);
    const [showNetworkModal, setShowNetworkModal] = useState(false);

    const { business, currency: currentCurrency } = useSelector((s) => s);
    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;

    // Backend-calculated fee & limits
    const [feeLoading, setFeeLoading] = useState(false);
    const [txnFee, setTxnFee] = useState(0); // absolute fee returned by backend
    const [minAmount, setMinAmount] = useState(0);
    const [maxAmount, setMaxAmount] = useState(0);
    const [dailyLimit, setDailyLimit] = useState(0);
    const [monthlyLimit, setMonthlyLimit] = useState(0);
    const [currentDaily, setCurrentDaily] = useState(0);
    const [currentMonthly, setCurrentMonthly] = useState(0);
    const [networks, setNetworks] = useState([]);

    const LOCALE = "en";

    const NUMBER_FORMAT_OPTS = useMemo(
        () => ({
            locales: LOCALE,
            maximumFractionDigits: 8,
        }),
        []
    );

    const formatted = useMemo(() => {
        return amount ? format(amount, NUMBER_FORMAT_OPTS) : "";
    }, [amount, NUMBER_FORMAT_OPTS]);

    const handleAmountChange = (e) => {
        const v = e.target.value; // formatted text from the input
        const raw = unformat(v || "", LOCALE); // raw numeric string

        setAmount(raw);
        dispatch(setCryptoAmount(raw)); // keep redux in sync if you want it live
    };

    const numericAmount = useMemo(() => {
        const n = Number.parseFloat(amount || "0");
        return Number.isFinite(n) ? n : 0;
    }, [amount]);

    // Fetch fee/limits whenever amount or currency changes
    useEffect(() => {
        if (!businessId || !currentCurrency) return;

        let cancelled = false;
        (async () => {
            try {
                setFeeLoading(true);
                const cc = String(currentCurrency).toUpperCase();
                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: cc,
                        currencyTo: cc,
                        amount: numericAmount || 0,
                    },
                });

                const d = res?.data ?? res ?? {};
                if (!cancelled) {
                    setTxnFee(Number(d.transactionFee) || 0);
                    setMinAmount(Number(d.minimumTransferAmount) || 0);
                    setMaxAmount(Number(d.maximumTransferAmount) || 0);
                    setDailyLimit(Number(d.dailyTransactionLimit) || 0);
                    setMonthlyLimit(Number(d.monthlyTransactionLimit) || 0);
                    setCurrentDaily(Number(d.currentDailyTransfer) || 0);
                    setCurrentMonthly(Number(d.currentMonthlyTransfer) || 0);
                }
            } catch {
                if (!cancelled) {
                    // keep previous values to avoid jarring UI resets
                    setTxnFee(0);
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [businessId, currentCurrency, numericAmount]);

    useEffect(() => {
        if (!currentCurrency || !businessId) return;

        let cancelled = false;
        (async () => {
            try {
                const list = await fetchCoinNetworks({
                    currency: String(currentCurrency).toUpperCase(),
                    businessId,
                });
                if (!cancelled) setNetworks(Array.isArray(list) ? list : []);
            } finally {
                //    console.log(s);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentCurrency, businessId]);

    // Reset other flows
    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
    }, [dispatch]);

    // Ensure token set (USDT/USDC)
    useEffect(() => {
        if (currentCurrency && (currentCurrency === "USDT" || currentCurrency === "USDC")) {
            dispatch(setCryptoToken(currentCurrency));
        }
    }, [currentCurrency, dispatch]);

    const selectedNetworkBalance = useMemo(() => {
        if (!network) return null;

        const selectedKey = String(network?.id || network?.name || "").toLowerCase();
        const matched = networks.find((item) => {
            const itemId = String(item?.id || "").toLowerCase();
            const itemName = String(item?.name || "").toLowerCase();
            return itemId === selectedKey || itemName === selectedKey;
        });

        const value = Number(matched?.balance);
        return Number.isFinite(value) ? value : null;
    }, [network, networks]);

    // Derived checks
    const availableBalance =
        selectedNetworkBalance !== null ? selectedNetworkBalance : Number(balance || 0);
    const feeValue = Number(txnFee) || 0; // absolute fee in the same crypto as amount
    const totalToDeduct = Math.max(0, numericAmount + feeValue);
    const insufficientFunds = totalToDeduct > availableBalance;

    // Limits & warnings
    const dailyRemaining = Math.max(0, dailyLimit - currentDaily);
    const monthlyRemaining = Math.max(0, monthlyLimit - currentMonthly);
    const exceedDaily = dailyLimit > 0 && numericAmount > dailyRemaining;
    const exceedMonthly = monthlyLimit > 0 && numericAmount > monthlyRemaining;

    const WARN_PCT = 0.9;
    const nearDaily =
        dailyLimit > 0 &&
        !exceedDaily &&
        numericAmount > 0 &&
        (currentDaily + numericAmount) / dailyLimit >= WARN_PCT;

    const nearMonthly =
        monthlyLimit > 0 &&
        !exceedMonthly &&
        numericAmount > 0 &&
        (currentMonthly + numericAmount) / monthlyLimit >= WARN_PCT;

    const tooLow = numericAmount > 0 && numericAmount < (minAmount || 0);
    const tooHigh = maxAmount > 0 && numericAmount > maxAmount;

    const hasValidAmount =
        numericAmount > 0 &&
        !insufficientFunds &&
        !tooLow &&
        !tooHigh &&
        !exceedDaily &&
        !exceedMonthly;

    const handleNetworkSelect = () => setShowNetworkModal(true);

    const handleContinue = () => {
        dispatch(setCryptoAmount(amount));
        dispatch(setCryptoNetwork(network?.id || network?.name || ""));
        if (currentCurrency === "USDT" || currentCurrency === "USDC") {
            dispatch(setCryptoToken(currentCurrency));
        }
        navigate("/payment/send-crypto/crypto-wallet/recipient-info");
    };

    const cc = String(currentCurrency || "").toUpperCase();

    return (
        <OnboardingLayout title="Send to external wallet" showSkip={false}>
            <div className="max-w-2xl mx-auto mt-10 space-y-10 md:px-4 pb-32 md:pb-0">
                <PaymentStepper currentStep={0} />

                <h2 className="text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white p-6 rounded-2xl space-y-6">
                    {/* Amount */}
                    <div className="flex items-center gap-2">
                        <div
                            className={`border-b w-full ${
                                insufficientFunds ? "border-red-500" : "border-black"
                            }`}
                        >
                            <InputNumberFormat
                                value={formatted}
                                onChange={handleAmountChange}
                                locales={LOCALE}
                                maximumFractionDigits={8}
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                className="!shadow-none w-full font-thin text-7xl outline-none caret-[#6F00FF] placeholder:text-[#C4C6DE] bg-transparent"
                                aria-invalid={insufficientFunds}
                                aria-describedby={insufficientFunds ? "amount-error" : undefined}
                            />
                        </div>

                        <CurrencyToggle
                            className="w-full text-sm bg-transparent"
                            dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                            fetchWallet
                            businessId={businessId}
                            isDisabled={!isBusinessApproved}
                            hideLabel
                        />
                    </div>

                    {/* Balance + validation */}
                    <div
                        className={`flex ${
                            insufficientFunds ? "justify-between" : "justify-between items-start"
                        }`}
                    >
                        <div className="text-sm space-y-1">
                            {exceedDaily && (
                                <div role="alert" className="text-red-600">
                                    This amount exceeds your <b>daily limit</b>. Remaining today:{" "}
                                    {dailyRemaining.toLocaleString()} {cc}.
                                </div>
                            )}
                            {exceedMonthly && (
                                <div role="alert" className="text-red-600">
                                    This amount exceeds your <b>monthly limit</b>. Remaining this
                                    month: {monthlyRemaining.toLocaleString()} {cc}.
                                </div>
                            )}
                            {insufficientFunds && (
                                <div id="amount-error" className="text-red-600" role="alert">
                                    Insufficient balance. Available:{" "}
                                    {availableBalance.toLocaleString()} {cc}
                                </div>
                            )}
                            {tooLow && (
                                <div className="text-red-600" role="alert">
                                    Minimum transfer amount is {minAmount.toLocaleString()} {cc}
                                </div>
                            )}
                            {tooHigh && (
                                <div className="text-red-600" role="alert">
                                    Maximum transfer amount is {maxAmount.toLocaleString()} {cc}
                                </div>
                            )}
                            {!exceedDaily &&
                                !exceedMonthly &&
                                !insufficientFunds &&
                                !tooLow &&
                                !tooHigh &&
                                numericAmount > 0 && (
                                    <>
                                        {nearDaily && (
                                            <div className="text-amber-600">
                                                Heads-up: this will put you close to your{" "}
                                                <b>daily</b> limit. Remaining today:{" "}
                                                {dailyRemaining.toLocaleString()} {cc}.
                                            </div>
                                        )}
                                        {nearMonthly && (
                                            <div className="text-amber-600">
                                                Heads-up: this will put you close to your{" "}
                                                <b>monthly</b> limit. Remaining this month:{" "}
                                                {monthlyRemaining.toLocaleString()} {cc}.
                                            </div>
                                        )}
                                    </>
                                )}
                        </div>

                        <div className="text-[#6B7280] text-sm">
                            <span>
                                {loading
                                    ? "Loading..."
                                    : `Balance: ${availableBalance.toLocaleString()} ${cc}`}
                            </span>

                            <span className="mb-2">
                                <InfoTooltip
                                    message={
                                        network
                                            ? `This is your ${currentCurrency} balance on ${network?.id || network?.name}`
                                            : `This is your total ${currentCurrency} balance`
                                    }
                                />
                            </span>
                        </div>
                    </div>

                    {/* Network */}
                    <div className="flex justify-between items-center border-t border-[#E5E7EB]">
                        <div>
                            <p className="text-[#161827] font-[450] text-[16px]">
                                {network ? (
                                    <>
                                        sending via {network.name || network.id || "—"} (
                                        {network.label || network.shortName || "—"})
                                    </>
                                ) : (
                                    "No network selected"
                                )}
                            </p>
                        </div>

                        <button
                            onClick={handleNetworkSelect}
                            className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                        >
                            {network ? "Change network" : "Select network"}
                            <ArrowRight2 size={18} />
                        </button>
                    </div>

                    {/* Fee & total */}
                    <div className="space-y-2 text-[16px] font-[400]">
                        <div className="flex justify-between">
                            <p className="text-[#161827]">Fee</p>
                            <p className="text-[#4B5563] ">
                                {feeLoading
                                    ? "Fetching fee…"
                                    : `${(feeValue || 0).toLocaleString()} ${cc}`}
                            </p>
                        </div>
                        <div className="flex justify-between text-[16px] font-[400] text-[#161827]">
                            <p>Total to deduct</p>
                            <p>
                                {totalToDeduct.toLocaleString()} {cc}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Desktop footer */}
                <div className="max-w-2xl mx-auto hidden md:block">
                    <div className="flex justify-between items-center border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
                            <Timer1 size="22" color="#6F739B" />
                            <span>Arrives in minutes</span>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                className="text-[#111827] text-[16px] font-[450]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!hasValidAmount || !network || feeLoading}
                                className={`rounded-xl px-6 py-2 text-white font-semibold text-[16px] ${
                                    !hasValidAmount || !network || feeLoading
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF] hover:bg-[#6A1BFF]"
                                }`}
                                onClick={handleContinue}
                                title={
                                    feeLoading
                                        ? "Calculating fee…"
                                        : !network
                                          ? "Select network"
                                          : tooLow
                                            ? `Minimum is ${minAmount.toLocaleString()} ${cc}`
                                            : tooHigh
                                              ? `Maximum is ${maxAmount.toLocaleString()} ${cc}`
                                              : exceedDaily
                                                ? `Exceeds daily limit. Remaining: ${dailyRemaining.toLocaleString()} ${cc}`
                                                : exceedMonthly
                                                  ? `Exceeds monthly limit. Remaining: ${monthlyRemaining.toLocaleString()} ${cc}`
                                                  : insufficientFunds
                                                    ? "Insufficient funds"
                                                    : !hasValidAmount
                                                      ? "Enter a valid amount"
                                                      : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile footer */}
                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">Arrives in minutes</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                className="text-[#111827] text-[16px] font-[450] w-[40%]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>

                            <button
                                disabled={!hasValidAmount || !network || feeLoading}
                                onClick={handleContinue}
                                className={`h-12 rounded-xl px-5 font-semibold text-white text-[16px] w-[48%] ${
                                    !hasValidAmount || !network || feeLoading
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF]"
                                }`}
                                title={
                                    feeLoading
                                        ? "Calculating fee…"
                                        : !network
                                          ? "Select network"
                                          : tooLow
                                            ? `Minimum is ${minAmount.toLocaleString()} ${cc}`
                                            : tooHigh
                                              ? `Maximum is ${maxAmount.toLocaleString()} ${cc}`
                                              : exceedDaily
                                                ? `Exceeds daily limit. Remaining: ${dailyRemaining.toLocaleString()} ${cc}`
                                                : exceedMonthly
                                                  ? `Exceeds monthly limit. Remaining: ${monthlyRemaining.toLocaleString()} ${cc}`
                                                  : insufficientFunds
                                                    ? "Insufficient funds"
                                                    : !hasValidAmount
                                                      ? "Enter a valid amount"
                                                      : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectNetworkModal
                isOpen={showNetworkModal}
                onClose={() => setShowNetworkModal(false)}
                onSelect={(net) => {
                    setNetwork(net);
                    setShowNetworkModal(false);
                }}
                selectedId={network?.id}
                currency={currentCurrency}
                businessId={businessId}
            />
        </OnboardingLayout>
    );
};

export default CryptoWalletTransfer;
